import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import VehicleClassBadge from "@/components/VehicleClassBadge";
import { linkifyPhones } from "@/lib/linkify";
import axios from "axios";
import { toast } from "sonner";
import { Check, MapPin, Phone, User, Calendar, Car, FileText, Plane, Navigation, CheckCircle2, MessageSquare, Clock, RefreshCw, ShieldCheck, Sparkles, X, Sun, Moon, Plus, Minus, ExternalLink, Camera, Upload } from "lucide-react";

// "UAL 2088" → https://www.flightaware.com/live/flight/UAL2088
const flightAwareUrl = (fn) => `https://www.flightaware.com/live/flight/${(fn || "").replace(/\s+/g, "").toUpperCase()}`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

const STEPS = [
  { key: "accepted", label: "Trip Accepted", desc: "Acknowledge the assignment" },
  { key: "en_route", label: "En Route to Pickup", desc: "On the way to the customer" },
  { key: "on_location", label: "On Location", desc: "Arrived at pickup" },
  { key: "customer_in_car", label: "Customer In Car", desc: "Trip in progress" },
  { key: "completed", label: "Dropped Off", desc: "Trip completed · sends thank-you" },
];

function statusIndex(s) {
  const i = STEPS.findIndex(x => x.key === s);
  return i < 0 ? -1 : i;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function DriverTrip() {
  const { token } = useParams();
  const [trip, setTrip] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [locShared, setLocShared] = useState(false);
  const [locErr, setLocErr] = useState("");
  const [nameBoardOpen, setNameBoardOpen] = useState(false);

  const reload = async () => {
    try {
      const r = await publicApi.get(`/driver-trip/${token}`);
      setTrip(r.data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to load trip");
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [token]);

  // Auto-poll the trip every 60s so any dispatcher-side edits (M&G toggle,
  // driver reassignment, notes change, etc.) show up on the driver's phone
  // without requiring a manual refresh.
  useEffect(() => {
    if (!token) return undefined;
    const id = setInterval(reload, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Live location push — runs whenever the driver is en_route / on_location / customer_in_car.
  // Uses browser geolocation (HTTPS-only). Updates customer's /track page in real time.
  const isActiveTrip = trip && ["en_route", "on_location", "customer_in_car"].includes(trip.status);
  useEffect(() => {
    if (!isActiveTrip) return undefined;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setLocErr("Geolocation not supported on this device");
      return undefined;
    }
    let watchId = null;
    let lastSent = 0;
    const send = async (lat, lng, heading, speed) => {
      try {
        await publicApi.post(`/driver-trip/${token}/location`, {
          lat, lng,
          heading: heading ?? null,
          speed_mph: speed != null ? speed * 2.23694 : null,  // m/s → mph
        });
        setLocShared(true);
        setLocErr("");
      } catch (e) {
        // silent — keep retrying on next position event
      }
    };
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastSent < 12000) return;  // throttle to ~5 pings/min
        lastSent = now;
        send(pos.coords.latitude, pos.coords.longitude, pos.coords.heading, pos.coords.speed);
      },
      (geoErr) => {
        setLocErr(geoErr.code === 1
          ? "Location permission denied — customer can't see your real-time position"
          : "Couldn't read GPS — please enable location services");
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => {
      setLocShared(false);
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };
  }, [isActiveTrip, token]);

  const extend = async (minutes) => {
    setBusy(true);
    try {
      const r = await publicApi.post(`/driver-trip/${token}/extend`, { minutes });
      setTrip(r.data);
      toast.success(`Trip extended — now booked for ${r.data.hours} hrs`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not extend the trip");
    } finally {
      setBusy(false);
    }
  };

  const advance = async (statusKey) => {
    setBusy(true);
    try {
      const r = await publicApi.post(`/driver-trip/${token}/status`, { status: statusKey });
      setTrip(r.data);
      const labels = {
        accepted: "Trip accepted",
        en_route: "Marked en route",
        on_location: "Marked on location",
        customer_in_car: "Customer in car",
        completed: "Trip completed — thank you message sent to customer",
      };
      toast.success(labels[statusKey] || "Status updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    } finally {
      setBusy(false);
    }
  };

  const markAtBaggageClaim = async () => {
    setBusy(true);
    try {
      const r = await publicApi.post(`/driver-trip/${token}/at-baggage-claim`);
      setTrip(r.data);
      toast.success("Customer notified · SMS sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not notify customer");
    } finally {
      setBusy(false);
    }
  };

  const recordStopEvent = async (stopIndex, action) => {
    setBusy(true);
    try {
      const r = await publicApi.post(`/driver-trip/${token}/stop-event`, { stop_index: stopIndex, action });
      setTrip(r.data);
      toast.success(action === "arrived" ? `Arrived at stop ${stopIndex + 1} — dispatch can see your wait time` : `Left stop ${stopIndex + 1}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record stop");
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <Shell>
        <div className="max-w-md mx-auto text-center mt-24 px-6" data-testid="driver-trip-error">
          <h1 className="font-serif text-3xl text-[#DC2626] mb-3">Link invalid</h1>
          <p className="text-sm text-[#52525b]">{err}</p>
          <p className="text-xs text-[#71717a] mt-6">Call dispatch at (414) 333-6275 if you need help.</p>
        </div>
      </Shell>
    );
  }
  if (!trip) {
    return (
      <Shell>
        <div className="text-center mt-24 text-sm text-[#52525b]" data-testid="driver-trip-loading">Loading your trip…</div>
      </Shell>
    );
  }

  const currentIdx = statusIndex(trip.status);
  const nextStep = currentIdx < 0 ? STEPS[0] : (STEPS[currentIdx + 1] || null);
  const isDone = trip.status === "completed";
  const isWaiting = trip.status === "on_location";

  // Build a Google Maps "Navigate to…" URL.
  // Before pickup we route to pickup; after on_location (en route with passenger) → drop-off.
  const navTarget = (() => {
    if (["accepted", "en_route"].includes(trip.status) || !trip.status) return trip.pickup_address;
    if (["on_location", "customer_in_car"].includes(trip.status)) {
      return trip.dropoff_address || trip.pickup_address;
    }
    return trip.dropoff_address || trip.pickup_address;
  })();
  const navLabel = ["accepted", "en_route"].includes(trip.status) ? "Navigate to Pickup" : "Navigate to Drop-off";
  const navUrl = navTarget
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(navTarget)}&travelmode=driving`
    : null;

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-8 pb-24" data-testid="driver-trip-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#DC2626] font-medium">Driver · Trip {trip.confirmation_code}</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#18181b] mt-3 leading-tight">
            {isDone ? "Trip complete" : (trip.customer_name || "Customer")}
          </h1>
          <div className="text-sm text-[#52525b] mt-2">
            {fmtDateTime(trip.pickup_datetime)}
          </div>
          {/* Trip Manifest — law-enforcement-ready PDF, one tap to open. */}
          <a
            data-testid="driver-trip-manifest-btn"
            href={`${API}/driver-trip/${token}/manifest.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 bg-[#18181b] text-white text-xs font-bold uppercase tracking-[0.22em] hover:bg-[#DC2626] transition-colors"
          >
            <ShieldCheck size={14}/> Trip Manifest
          </a>
        </div>

        {/* Done banner */}
        {isDone && (
          <div className="border border-[#DC2626]/40 bg-[#DC2626]/5 rounded p-5 text-center mb-8" data-testid="trip-done-banner">
            <CheckCircle2 className="inline-block text-[#DC2626] mb-2" size={32} />
            <p className="text-sm text-[#18181b]">A thank-you text & email was just sent to {trip.customer_name || "the customer"}.</p>
          </div>
        )}

        {/* Trip details */}
        <div className="border border-[#e4e4e7] rounded p-5 mb-8 bg-white space-y-3" data-testid="trip-details">
          <Row icon={<User size={16}/>} label={trip.guest_name ? "Booked by" : "Customer"} value={trip.customer_name || "—"} />
          {trip.guest_name && (
            <div data-testid="driver-guest-info" className="border border-[#DC2626]/30 bg-[#DC2626]/5 rounded p-3 space-y-2">
              <Row icon={<User size={16}/>} label={`Guest${trip.guest_count > 1 ? `s (${trip.guest_count})` : ""}`} value={<span className="font-medium">{trip.guest_name}</span>} />
              {trip.guest_phone && (
                <Row icon={<Phone size={16}/>} label="Guest phone" value={
                  <a href={`tel:${trip.guest_phone}`} className="text-[#DC2626] font-medium">{trip.guest_phone}</a>
                } />
              )}
            </div>
          )}
          {trip.customer_phone && (
            <Row icon={<Phone size={16}/>} label="Phone" value={
              <a href={`tel:${trip.customer_phone}`} className="text-[#DC2626] font-medium">{trip.customer_phone}</a>
            } />
          )}
          <Row icon={<Calendar size={16}/>} label="Pickup time" value={fmtDateTime(trip.pickup_datetime)} />
          <Row icon={<MapPin size={16}/>} label="Pickup" value={trip.pickup_address || "—"} />
          {(trip.stops || []).map((s, i) => (
            <Row key={i} icon={<MapPin size={16}/>} label={`Stop ${i+1}`} value={s} />
          ))}
          {trip.trip_type !== "hourly" && (
            <Row icon={<MapPin size={16}/>} label="Drop-off" value={trip.dropoff_address || "—"} />
          )}
          {trip.trip_type === "hourly" && (
            <Row icon={<MapPin size={16}/>} label="Hours" value={String(trip.hours || "—")} />
          )}
          {trip.flight_number && (
            <Row icon={<Plane size={16}/>} label="Flight" value={
              <a
                data-testid="flight-live-link"
                href={flightAwareUrl(trip.flight_number)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[#DC2626] font-medium underline underline-offset-2"
              >
                {trip.flight_number}
                <ExternalLink size={12}/>
              </a>
            } />
          )}
          {trip.vehicle_name && (
            <Row icon={<Car size={16}/>} label="Vehicle" value={
              <span className="inline-flex items-center gap-2">
                {trip.vehicle_name}
                <VehicleClassBadge variant="light" vehicleId={trip.vehicle_id} vehicleName={trip.vehicle_name}/>
              </span>
            } />
          )}
          {(trip.passengers || trip.luggage) && (
            <Row icon={<User size={16}/>} label="Passengers · Bags" value={`${trip.passengers || "—"} pax · ${trip.luggage || 0} bags`} />
          )}
          {trip.notes && (
            <Row icon={<FileText size={16}/>} label="Notes" value={linkifyPhones(trip.notes)} />
          )}
        </div>

        {/* Trip photos & receipts — token upload, no login needed */}
        <TripPhotosCard token={token}/>

        {/* Meet & Greet — shown only when customer opted in */}
        {trip.meet_and_greet && !isDone && (
          <div
            className="border-2 border-[#D4AF37]/60 bg-[#D4AF37]/5 rounded p-5 mb-8"
            data-testid="meet-greet-panel"
          >
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="text-[#D4AF37] flex-shrink-0 mt-1" size={20}/>
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-bold">Meet &amp; Greet</div>
                <div className="text-sm text-[#18181b] mt-1">
                  Client requested a name-board greeting. Head inside baggage claim / lobby and hold up the sign — no curbside pickup.
                </div>
                {trip.at_baggage_claim_at && (
                  <div className="text-xs text-[#059669] mt-2 font-medium">
                    ✓ Customer notified at {new Date(trip.at_baggage_claim_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                data-testid="open-name-board-btn"
                onClick={() => setNameBoardOpen(true)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#18181b] text-white text-xs font-bold uppercase tracking-[0.22em] hover:bg-[#D4AF37] hover:text-black transition-colors"
              >
                <User size={14}/> Open Name Board
              </button>
              <button
                data-testid="at-baggage-claim-btn"
                onClick={markAtBaggageClaim}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-[0.22em] hover:bg-[#B8952E] disabled:opacity-50 transition-colors"
              >
                <MapPin size={14}/> {trip.at_baggage_claim_at ? "Re-notify customer" : "I'm at Baggage Claim"}
              </button>
            </div>
          </div>
        )}

        {/* Stops — arrival/departure tracking so dispatch can bill wait time */}
        {(trip.stops || []).length > 0 && !isDone && (
          <div className="border-2 border-[#DC2626]/30 bg-white rounded p-5 mb-8" data-testid="stops-panel">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#DC2626] mb-1">Stops</div>
            <div className="text-xs text-[#71717a] mb-4">
              Tap when you arrive and when you leave each stop — dispatch sees your wait time.
            </div>
            <div className="space-y-3">
              {(trip.stops || []).map((s, i) => {
                const ev = (trip.stop_events || []).find(e => e.stop_index === i);
                const fmtT = (iso) => iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
                const liveWaitMin = ev?.arrived_at && !ev?.departed_at
                  ? Math.max(0, Math.round((Date.now() - new Date(ev.arrived_at).getTime()) / 60000))
                  : null;
                const canAct = trip.status === "customer_in_car";
                return (
                  <div key={i} className="border border-[#e4e4e7] rounded p-3" data-testid={`stop-panel-${i}`}>
                    <div className="text-sm font-medium text-[#18181b] mb-2">Stop {i + 1} · <span className="font-normal">{s}</span></div>
                    {!ev?.arrived_at && (
                      <button
                        data-testid={`stop-arrived-${i}`}
                        onClick={() => recordStopEvent(i, "arrived")}
                        disabled={busy || !canAct}
                        title={canAct ? "" : "Available once the customer is in the car"}
                        className="w-full px-4 py-2.5 bg-[#18181b] text-white text-xs font-bold uppercase tracking-[0.22em] hover:bg-[#DC2626] disabled:opacity-40 transition-colors"
                      >
                        Arrived at Stop {i + 1}
                      </button>
                    )}
                    {ev?.arrived_at && !ev?.departed_at && (
                      <div className="space-y-2">
                        <div className="text-xs text-[#DC2626] font-medium" data-testid={`stop-waiting-${i}`}>
                          ● At stop since {fmtT(ev.arrived_at)} · waiting {liveWaitMin} min
                        </div>
                        <button
                          data-testid={`stop-departed-${i}`}
                          onClick={() => recordStopEvent(i, "departed")}
                          disabled={busy}
                          className="w-full px-4 py-2.5 bg-[#DC2626] text-white text-xs font-bold uppercase tracking-[0.22em] hover:bg-[#B91C1C] disabled:opacity-40 transition-colors"
                        >
                          Leaving Stop {i + 1}
                        </button>
                      </div>
                    )}
                    {ev?.arrived_at && ev?.departed_at && (
                      <div className="text-xs text-[#059669] font-medium" data-testid={`stop-done-${i}`}>
                        ✓ {fmtT(ev.arrived_at)} – {fmtT(ev.departed_at)} · {ev.wait_minutes != null ? `${Math.round(ev.wait_minutes)} min at stop` : "recorded"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Status pipeline */}
        <div className="border border-[#e4e4e7] rounded p-5 mb-8 bg-white" data-testid="status-pipeline">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#DC2626] mb-4">Status</div>
          <ol className="space-y-3">
            {STEPS.map((s, i) => {
              const done = currentIdx >= i;
              const isCurrent = currentIdx === i;
              // Show the timestamp captured for this status transition (HH:MM AM/PM)
              const tsField = {
                accepted: "accepted_at",
                en_route: "en_route_at",
                on_location: "on_location_at",
                customer_in_car: "customer_in_car_at",
                completed: "completed_at",
              }[s.key];
              const ts = tsField && trip[tsField];
              const tsLabel = ts ? new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
              return (
                <li
                  key={s.key}
                  data-testid={`step-${s.key}`}
                  className={`flex items-start gap-3 ${done ? "" : "opacity-60"}`}
                >
                  <span className={`flex-shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    done
                      ? (isCurrent && !isDone ? "bg-[#DC2626] text-white" : "bg-[#DC2626] text-white")
                      : "bg-[#e4e4e7] text-[#71717a]"
                  }`}>
                    {done ? <Check size={14}/> : (i+1)}
                  </span>
                  <div className="flex-1 flex items-start justify-between gap-3">
                    <div>
                      <div className={`text-sm font-medium ${done ? "text-[#18181b]" : "text-[#52525b]"}`}>{s.label}</div>
                      <div className="text-xs text-[#71717a]">{s.desc}</div>
                    </div>
                    {tsLabel && (
                      <div
                        className={`text-xs font-mono tabular-nums tracking-tight whitespace-nowrap pt-0.5 ${done ? "text-[#DC2626]" : "text-[#a1a1aa]"}`}
                        data-testid={`step-${s.key}-time`}
                      >
                        {tsLabel}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Wait timer — only while driver is on-location waiting for customer */}
        {isWaiting && (
          <WaitTimer
            since={trip.on_location_at}
            graceMin={trip.wait_grace_minutes}
            category={trip.wait_grace_category}
            ratePerMin={trip.wait_rate_per_min}
          />
        )}

        {/* Flight tracking card — only when booking has a flight number */}
        {trip.flight_number && (
          <FlightCard
            token={token}
            ident={trip.flight_number}
            status={trip.flight_status}
            onRefresh={reload}
            tripStatus={trip.status}
            pickupCategory={trip.wait_grace_category}
          />
        )}

        {/* Trip Duration badge — shows once trip is completed (handy for hourly billing) */}
        {isDone && trip.trip_duration_seconds != null && (
          <TripDurationBadge seconds={trip.trip_duration_seconds} basis={trip.trip_duration_basis}/>
        )}

        {/* Quick-action row: Text · Call · Navigate (Google Maps) */}
        {!isDone && (
          <div className="grid grid-cols-3 gap-3 mb-6" data-testid="quick-actions">
            <a
              href={trip.customer_phone ? `sms:${trip.customer_phone}` : "#"}
              data-testid="action-text-customer"
              className={`flex items-center justify-center gap-2 border rounded py-3 text-xs uppercase tracking-[0.16em] font-medium ${
                trip.customer_phone ? "border-[#18181b] text-[#18181b] hover:bg-[#18181b] hover:text-white" : "border-[#e4e4e7] text-[#a1a1aa] pointer-events-none"
              } transition-colors`}
            >
              <MessageSquare size={14}/> Text
            </a>
            <a
              href={trip.customer_phone ? `tel:${trip.customer_phone}` : "#"}
              data-testid="action-call-customer"
              className={`flex items-center justify-center gap-2 border rounded py-3 text-xs uppercase tracking-[0.16em] font-medium ${
                trip.customer_phone ? "border-[#18181b] text-[#18181b] hover:bg-[#18181b] hover:text-white" : "border-[#e4e4e7] text-[#a1a1aa] pointer-events-none"
              } transition-colors`}
            >
              <Phone size={14}/> Call
            </a>
            <a
              href={navUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="action-navigate"
              className={`flex items-center justify-center gap-2 rounded py-3 text-xs uppercase tracking-[0.16em] font-semibold ${
                navUrl ? "bg-[#DC2626] text-white hover:bg-[#B91C1C]" : "bg-[#f1f1f1] text-[#a1a1aa] pointer-events-none"
              } transition-colors`}
            >
              <Navigation size={14}/> Navigate
            </a>
          </div>
        )}

        {/* Action button */}
        {!isDone && nextStep && (
          <div className="text-center">
            {["en_route", "on_location", "customer_in_car"].includes(trip.status) && (
              <div
                data-testid="location-share-status"
                className={`text-xs mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded border ${
                  locErr
                    ? "border-amber-400/60 bg-amber-50 text-amber-800"
                    : locShared
                      ? "border-emerald-500/40 bg-emerald-50 text-emerald-700"
                      : "border-[#e4e4e7] bg-[#f9fafb] text-[#52525b]"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${locShared && !locErr ? "bg-emerald-500 animate-pulse" : "bg-[#a1a1aa]"}`}/>
                {locErr
                  ? locErr
                  : locShared
                    ? "Sharing live location — customer can see your ETA in real time"
                    : "Waiting for GPS permission… please allow location access"}
              </div>
            )}
            <div>
              <button
                data-testid={`advance-${nextStep.key}-btn`}
                disabled={busy}
                onClick={() => advance(nextStep.key)}
                className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-8 py-4 font-semibold tracking-[0.16em] uppercase text-xs rounded hover:bg-[#B91C1C] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Navigation size={14}/>
                {`Mark · ${nextStep.label}`}
              </button>
            </div>
            <div className="text-xs text-[#71717a] mt-3">
              Tap when you&apos;ve actually performed this step. Customer sees real-time updates.
            </div>
          </div>
        )}

        {/* Reset / Re-open status (in case of mistake) */}
        {currentIdx > 0 && !isDone && (
          <div className="text-center mt-6">
            <button
              data-testid="undo-status-btn"
              disabled={busy}
              onClick={() => advance(STEPS[currentIdx - 1].key)}
              className="text-xs uppercase tracking-[0.18em] text-[#71717a] hover:text-[#DC2626] disabled:opacity-50"
            >
              ← Undo · back to {STEPS[currentIdx - 1].label}
            </button>
          </div>
        )}

        {/* Extend hourly charter — client keeping the car longer (no prices shown) */}
        {trip.trip_type === "hourly" && !isDone && trip.status !== "cancelled" && (
          <div className="mt-8 border-2 border-[#DC2626]/30 bg-white rounded p-5 text-center" data-testid="extend-trip-box">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#DC2626] font-bold mb-1">Client keeping the car longer?</div>
            <div className="text-sm text-[#18181b] font-medium mb-4">
              Booked: <span className="text-[#DC2626] font-bold" data-testid="extend-current-hours">{trip.hours} hrs</span> — extend the charter and dispatch updates automatically.
            </div>
            <div className="flex justify-center gap-3">
              <button
                data-testid="extend-30-btn"
                disabled={busy}
                onClick={() => extend(30)}
                className="border-2 border-[#DC2626] bg-white text-[#DC2626] px-6 py-3 text-xs font-bold uppercase tracking-[0.16em] rounded hover:bg-[#DC2626] hover:text-white transition-colors disabled:opacity-50"
              >+ 30 min</button>
              <button
                data-testid="extend-60-btn"
                disabled={busy}
                onClick={() => extend(60)}
                className="border-2 border-[#DC2626] bg-white text-[#DC2626] px-6 py-3 text-xs font-bold uppercase tracking-[0.16em] rounded hover:bg-[#DC2626] hover:text-white transition-colors disabled:opacity-50"
              >+ 1 hour</button>
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Name Board — driver holds phone up so client can spot it */}
      {nameBoardOpen && (
        <NameBoardOverlay
          customerName={trip.guest_name || trip.customer_name || "Guest"}
          companyName={trip.company_name}
          onClose={() => setNameBoardOpen(false)}
        />
      )}
    </Shell>
  );
}

/**
 * Fullscreen name board — used at airport baggage claim / hotel lobby.
 * Three high-contrast themes so the driver can pick whichever pops best
 * against ambient lighting (fluorescent baggage claim = LIGHT is easiest
 * to spot; night pickup or dim hotel lobby = DARK; upscale venues = GOLD).
 * Choice is persisted in localStorage.
 */
const NB_THEMES = {
  dark:  { bg: "#000000", fg: "#FFFFFF", accent: "#D4AF37", sub: "rgba(255,255,255,0.45)", subLight: "rgba(255,255,255,0.5)" },
  light: { bg: "#FFFFFF", fg: "#0A0A0A", accent: "#7A5A0F", sub: "rgba(10,10,10,0.55)", subLight: "rgba(10,10,10,0.6)" },
  gold:  { bg: "#0B1F3A", fg: "#F5E8B8", accent: "#F5C842", sub: "rgba(245,232,184,0.55)", subLight: "rgba(245,232,184,0.6)" },
};

function NameBoardOverlay({ customerName, companyName, onClose }) {
  const [themeKey, setThemeKey] = useState(() => {
    try { return localStorage.getItem("nameBoardTheme") || "dark"; } catch { return "dark"; }
  });
  const [sizeMul, setSizeMul] = useState(() => {
    try { return parseFloat(localStorage.getItem("nameBoardSize")) || 1.0; } catch { return 1.0; }
  });
  const theme = NB_THEMES[themeKey] || NB_THEMES.dark;

  const cycleTheme = () => {
    const keys = ["dark", "light", "gold"];
    const next = keys[(keys.indexOf(themeKey) + 1) % keys.length];
    setThemeKey(next);
    try { localStorage.setItem("nameBoardTheme", next); } catch {/* ignore */}
  };

  const bumpSize = (delta) => {
    setSizeMul(prev => {
      const next = Math.min(1.8, Math.max(0.6, +(prev + delta).toFixed(1)));
      try { localStorage.setItem("nameBoardSize", String(next)); } catch {/* ignore */}
      return next;
    });
  };
  // Scale helper — multiplies each clamp() max by sizeMul so text grows/shrinks together.
  const scale = (base) => `calc(${base} * ${sizeMul})`;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    let wakeLock = null;
    (async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLock = await navigator.wakeLock.request("screen");
        }
      } catch {/* ignore */}
    })();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "t" || e.key === "T") cycleTheme();
      if (e.key === "+" || e.key === "=") bumpSize(0.1);
      if (e.key === "-" || e.key === "_") bumpSize(-0.1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      if (wakeLock) { try { wakeLock.release(); } catch {/* ignore */} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, themeKey]);

  const ThemeIcon = themeKey === "light" ? Sun : (themeKey === "gold" ? Sparkles : Moon);
  const themeLabel = themeKey === "light" ? "Light" : (themeKey === "gold" ? "Gold" : "Dark");

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center px-6 transition-colors duration-300"
      style={{ backgroundColor: theme.bg, color: theme.fg }}
      data-testid="name-board-overlay"
      data-theme={themeKey}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Theme toggle — top left */}
      <button
        data-testid="name-board-theme-toggle"
        onClick={cycleTheme}
        className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 h-10 rounded-full text-[10px] uppercase tracking-[0.22em] font-medium transition-colors"
        style={{
          backgroundColor: themeKey === "light" ? "rgba(10,10,10,0.08)" : "rgba(255,255,255,0.12)",
          color: theme.fg,
        }}
        aria-label={`Switch name board theme (currently ${themeLabel})`}
      >
        <ThemeIcon size={14}/> {themeLabel}
      </button>
      {/* Close — top right */}
      <button
        data-testid="name-board-close"
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
        style={{ backgroundColor: themeKey === "light" ? "rgba(10,10,10,0.08)" : "rgba(255,255,255,0.12)", color: theme.fg }}
        aria-label="Close name board"
      >
        <X size={20}/>
      </button>

      {/* Size control — right edge, volume-style +/− stack */}
      <div
        className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-2 select-none"
        data-testid="name-board-size-control"
      >
        <button
          data-testid="name-board-size-up"
          onClick={() => bumpSize(0.1)}
          disabled={sizeMul >= 1.8}
          className="w-11 h-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
          style={{ backgroundColor: themeKey === "light" ? "rgba(10,10,10,0.08)" : "rgba(255,255,255,0.12)", color: theme.fg }}
          aria-label="Bigger name board text"
        >
          <Plus size={20}/>
        </button>
        <div
          data-testid="name-board-size-value"
          className="text-[9px] tracking-[0.22em] font-medium uppercase min-w-[40px] text-center"
          style={{ color: theme.sub }}
        >
          {Math.round(sizeMul * 100)}%
        </div>
        <button
          data-testid="name-board-size-down"
          onClick={() => bumpSize(-0.1)}
          disabled={sizeMul <= 0.6}
          className="w-11 h-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
          style={{ backgroundColor: themeKey === "light" ? "rgba(10,10,10,0.08)" : "rgba(255,255,255,0.12)", color: theme.fg }}
          aria-label="Smaller name board text"
        >
          <Minus size={20}/>
        </button>
      </div>

      <div className="text-center mb-6">
        <div
          className="uppercase font-semibold tracking-[0.28em]"
          style={{ color: theme.accent, fontSize: scale("clamp(1.25rem, 3.5vw, 2.25rem)") }}
        >
          Delux Limo · Trans Group
        </div>
      </div>
      <div className="w-full max-w-4xl text-center">
        <div
          className="uppercase font-bold mb-4 nameboard-flash"
          style={{ fontSize: scale("clamp(1.75rem, 5vw, 3rem)"), letterSpacing: "0.28em", color: "#DC2626" }}
          data-testid="name-board-welcome"
        >
          Welcome
        </div>
        <div
          className="font-serif leading-none break-words"
          style={{ fontSize: scale("clamp(3rem, 12vw, 8rem)"), color: theme.fg }}
          data-testid="name-board-customer"
        >
          {customerName}
        </div>
        {companyName && (
          <div
            className="mt-8 font-semibold tracking-wide"
            style={{ fontSize: scale("clamp(2rem, 6vw, 4rem)"), color: theme.accent }}
            data-testid="name-board-company"
          >
            {companyName}
          </div>
        )}
      </div>
      {/* Pulsing red welcome — smooth breathing (not a strobe, so it doesn't
          cause perceptual flicker on nearby text). */}
      <style>{`
        @keyframes nbPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.55; }
        }
        .nameboard-flash {
          animation: nbPulse 1.6s ease-in-out infinite;
          will-change: opacity;
        }
      `}</style>
    </div>
  );
}

function FlightCard({ token, ident, status, onRefresh, tripStatus, pickupCategory }) {
  const [busy, setBusy] = useState(false);
  const [autoOn, setAutoOn] = useState(false);
  const [lastAuto, setLastAuto] = useState(null);

  const refresh = async (silent = false) => {
    setBusy(true);
    try {
      await publicApi.post(`/driver-trip/${token}/refresh-flight`);
      if (onRefresh) await onRefresh();
      if (!silent) toast.success("Flight status refreshed");
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Could not refresh flight");
    } finally {
      setBusy(false);
    }
  };

  // Smart auto-refresh: poll every 5 min when driver is heading to an airport
  // pickup AND the flight is arriving within 2 hours.
  useEffect(() => {
    const isPreArrival = ["accepted", "en_route"].includes(tripStatus);
    const isAirport = pickupCategory === "Airport";
    if (!isPreArrival || !isAirport) { setAutoOn(false); return undefined; }
    const arriveIso = status?.estimated_in || status?.actual_in || status?.scheduled_in;
    if (!arriveIso) { setAutoOn(false); return undefined; }
    const minsToArrival = (new Date(arriveIso).getTime() - Date.now()) / 60000;
    if (minsToArrival > 120 || minsToArrival < -30) { setAutoOn(false); return undefined; }
    setAutoOn(true);
    const id = setInterval(() => {
      setLastAuto(new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }));
      refresh(true);
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [tripStatus, pickupCategory, status?.estimated_in, status?.actual_in, status?.scheduled_in]);

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); } catch { return iso; }
  };
  const fs = status || {};
  const delay = fs.delay_minutes;
  const delayLabel = delay == null
    ? null
    : delay > 0 ? `Delayed ${delay} min` : delay < 0 ? `Early ${Math.abs(delay)} min` : "On time";
  const delayCls = delay == null
    ? "text-[#71717a]"
    : delay > 10 ? "text-red-600" : delay > 0 ? "text-amber-600" : "text-emerald-600";

  return (
    <div className="border border-[#e4e4e7] bg-white rounded p-4 mb-6" data-testid="flight-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Plane size={18} className="text-[#DC2626]"/>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717a] font-medium flex items-center gap-2">
              Flight tracking
              {autoOn && (
                <span data-testid="flight-auto-badge" className="inline-flex items-center gap-1 text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-full px-2 py-0.5 normal-case tracking-normal font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
                  Auto-refresh every 5 min
                </span>
              )}
            </div>
            <a
              data-testid="flight-card-live-link"
              href={flightAwareUrl(ident)}
              target="_blank"
              rel="noreferrer"
              className="text-lg font-mono font-semibold text-[#18181b] inline-flex items-center gap-1.5 underline underline-offset-2 decoration-[#DC2626]/50 hover:text-[#DC2626] transition-colors"
              title="Open live status on FlightAware"
            >
              {ident}
              <ExternalLink size={13} className="text-[#DC2626]"/>
            </a>
          </div>
        </div>
        <button
          data-testid="refresh-flight"
          onClick={() => refresh(false)}
          disabled={busy}
          className="text-[10px] uppercase tracking-[0.18em] text-[#DC2626] border border-[#DC2626] px-2.5 py-1.5 rounded hover:bg-[#DC2626] hover:text-white transition-colors disabled:opacity-50"
        >
          {busy ? "…" : (<><RefreshCw size={11} className="inline mr-1"/>Refresh</>)}
        </button>
      </div>
      {status ? (
        <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
          {/cancel/i.test(fs.status || "") && (
            <div data-testid="flight-cancelled-driver-banner"
              className="col-span-2 border border-red-300 bg-red-50 rounded p-2 text-xs text-red-700 font-semibold">
              ✈ Flight CANCELLED — dispatch is getting the customer's new flight. Hold tight for updates.
            </div>
          )}
          <div><span className="text-[#71717a]">Route</span><div className="text-[#18181b]">{fs.origin || "—"} → {fs.destination || "—"}</div></div>
          <div><span className="text-[#71717a]">Status</span><div className="text-[#18181b]">{fs.status || "—"}</div></div>
          <div><span className="text-[#71717a]">Scheduled arrival</span><div className="text-[#18181b]">{fmt(fs.scheduled_in)}</div></div>
          <div><span className="text-[#71717a]">Est / actual arrival</span><div className="text-[#18181b]">{fmt(fs.actual_in || fs.estimated_in)}</div></div>
          {delayLabel && (
            <div className="col-span-2 mt-1">
              <span className={`text-xs font-semibold ${delayCls}`}>{delayLabel}</span>
              {fs.source === "MOCKED" && <span className="text-[10px] text-[#a1a1aa] ml-2">(mock data — set FLIGHTAWARE_API_KEY for live)</span>}
            </div>
          )}
          {lastAuto && (
            <div className="col-span-2 text-[10px] text-emerald-700/80 mt-1">Last auto-refresh: {lastAuto}</div>
          )}
        </div>
      ) : (
        <div className="text-xs text-[#71717a] mt-3">No flight status yet — tap Refresh to fetch from FlightAware.</div>
      )}
    </div>
  );
}

function TripDurationBadge({ seconds, basis }) {
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor((seconds % 3600) / 60);
  const label = hh > 0 ? `${hh}h ${mm}m` : `${mm}m`;
  const basisLabel = (
    basis === "customer_in_car" ? "Customer In Car → Dropped Off" :
    basis === "on_location"     ? "On Location → Dropped Off" :
                                  "Accepted → Dropped Off"
  );
  return (
    <div
      data-testid="trip-duration-badge"
      className="border border-emerald-500/40 bg-emerald-50 rounded p-4 mb-6 text-center"
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-medium">Total ride duration</div>
      <div className="text-3xl font-mono font-bold text-emerald-900 tabular-nums mt-1">{label}</div>
      <div className="text-xs text-emerald-700/80 mt-1">{basisLabel}</div>
    </div>
  );
}

/**
 * Trip photos & receipts — token-authed upload from the driver's phone.
 * No login required: the driver-trip token IS the credential. Files land in
 * GridFS as trip_attachments and show up on the dispatcher's reservation page.
 */
const TRIP_PHOTO_TYPES = [
  ["photo", "Photo"], ["receipt", "Receipt"], ["damage", "Damage"],
  ["signed_doc", "Signed doc"], ["other", "Other"],
];

function TripPhotosCard({ token }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [docType, setDocType] = useState("photo");
  const inputRef = useRef(null);

  const reload = async () => {
    try {
      const r = await publicApi.get(`/driver-trip/${token}/files`);
      setFiles(r.data || []);
    } catch { /* section stays empty */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [token]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await publicApi.post(`/driver-trip/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded — dispatch can see it now");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const fileUrl = (f) => `${API}/files/${f.file_id}/content?token=${token}`;

  return (
    <div className="border border-[#e4e4e7] bg-white rounded p-5 mb-8" data-testid="trip-photos-card">
      <div className="flex items-center gap-3 flex-wrap mb-1">
        <Camera size={16} className="text-[#DC2626]"/>
        <span className="text-[10px] tracking-[0.28em] uppercase text-[#DC2626] font-medium">Trip photos &amp; receipts</span>
        <select
          data-testid="trip-photo-type"
          value={docType}
          onChange={e => setDocType(e.target.value)}
          className="border border-[#e4e4e7] rounded px-2 py-1 text-[11px] text-[#18181b] bg-white"
        >
          {TRIP_PHOTO_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button
          data-testid="trip-photo-upload-btn"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#18181b] text-white text-[10px] font-bold uppercase tracking-[0.18em] rounded hover:bg-[#DC2626] disabled:opacity-50 transition-colors"
        >
          <Upload size={12}/>{busy ? "Uploading…" : "Add photo"}
        </button>
        <input
          ref={inputRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden"
          onChange={e => { upload(e.target.files?.[0]); e.target.value = ""; }}
        />
      </div>
      <div className="text-xs text-[#71717a] mb-3">
        Snap tolls, parking receipts, or vehicle condition — dispatch sees them instantly.
      </div>
      {files.length === 0 ? (
        <div className="text-xs text-[#a1a1aa]" data-testid="trip-photos-empty">No photos yet.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {files.map(f => {
            const isImg = f.content_type?.startsWith("image/");
            return (
              <a
                key={f.file_id}
                href={fileUrl(f)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`trip-photo-${f.file_id}`}
                className="block border border-[#e4e4e7] rounded overflow-hidden hover:border-[#DC2626] transition-colors"
                title={f.filename}
              >
                {isImg ? (
                  <img src={fileUrl(f)} alt={f.filename} className="h-20 w-full object-cover" loading="lazy"/>
                ) : (
                  <div className="h-20 flex items-center justify-center text-[#a1a1aa] bg-[#f9fafb]"><FileText size={22}/></div>
                )}
                <div className="px-1.5 py-1 text-[9px] uppercase tracking-[0.12em] text-[#71717a] truncate">{f.doc_type}</div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[#DC2626] mt-1">{icon}</div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717a]">{label}</div>
        <div className="text-sm text-[#18181b]">{value}</div>
      </div>
    </div>
  );
}

function WaitTimer({ since, graceMin, category, ratePerMin }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!since) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [since]);
  if (!since) return null;
  const startMs = new Date(since).getTime();
  if (Number.isNaN(startMs)) return null;
  const seconds = Math.max(0, Math.floor((now - startMs) / 1000));
  const graceSec = (graceMin || 10) * 60;
  const inGrace = seconds < graceSec;
  const remaining = inGrace ? graceSec - seconds : 0;
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor((seconds % 3600) / 60);
  const ss = seconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const text = hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
  const remText = `${pad(Math.floor(remaining / 60))}:${pad(remaining % 60)}`;

  return (
    <div
      data-testid="wait-timer"
      className={`border rounded p-4 mb-6 ${inGrace ? "border-amber-400/60 bg-amber-50" : "border-stone-400/60 bg-stone-50"}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Clock size={18} className={inGrace ? "text-amber-600" : "text-stone-600"}/>
          <div>
            <div className={`text-[10px] uppercase tracking-[0.22em] font-medium ${inGrace ? "text-amber-700" : "text-stone-700"}`}>
              {inGrace ? "Waiting at pickup · free time" : "Waiting at pickup"}
            </div>
            <div className={`text-2xl font-mono font-semibold tabular-nums mt-0.5 ${inGrace ? "text-amber-900" : "text-stone-900"}`}>{text}</div>
          </div>
        </div>
        <div className="text-right text-[11px]">
          <div className="uppercase tracking-[0.16em] text-[#71717a]">{category} · {graceMin} min free</div>
          {inGrace ? (
            <div className="text-amber-700 mt-1">Grace remaining: <b>{remText}</b></div>
          ) : (
            // Wait fee is dispatcher-side only — driver sees the clock keep
            // running but no $ amount. Dispatcher decides at settlement time
            // whether to apply, waive, or partial-credit.
            <div className="text-stone-700 mt-1">Grace exceeded · keep waiting</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-white text-[#18181b]" data-testid="driver-trip-shell">
      <header className="border-b border-[#e4e4e7] py-5 px-5 md:px-8 bg-white">
        <div className="flex items-center justify-center gap-4 md:gap-5">
          <img
            src="/logos/monogram.png"
            alt="Delux Limo Trans Group"
            className="w-14 h-14 md:w-16 md:h-16 object-contain flex-shrink-0"
            data-testid="driver-portal-logo"
          />
          <div className="text-center">
            <div className="font-serif text-xl md:text-3xl text-[#DC2626] tracking-[0.12em] leading-tight">Delux Limo Trans Group</div>
            <div className="text-[10px] md:text-[11px] text-[#52525b] tracking-[0.18em] uppercase mt-1">Premium Chauffeur &amp; Luxury Transportation</div>
            <div className="text-[10px] text-[#DC2626] tracking-[0.32em] uppercase mt-2 font-medium">Driver Portal</div>
          </div>
        </div>
      </header>
      {children}
      <footer className="border-t border-[#e4e4e7] py-6 text-center text-[10px] text-[#71717a] tracking-[0.18em] uppercase bg-white">
        Dispatch · (414) 333-6275 · deluxlimotransgroup.com
      </footer>
    </div>
  );
}
